import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import adminService from '../services/adminService';
import { toast } from 'react-toastify';

function UserDetailView() {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      try {
        // Fetch user details
        const userData = await adminService.getUserById(userId);
        setUser(userData.user || userData);
        
        // Fetch user certificates
        try {
          const certificatesData = await adminService.getUserCertificates(userId);
          console.log('Certificate data received:', certificatesData);
          
          // Extract certificates array from response with better logging
          let extractedCertificates = [];
          if (certificatesData?.certificates && Array.isArray(certificatesData.certificates)) {
            extractedCertificates = certificatesData.certificates;
            console.log(`Found ${extractedCertificates.length} certificates in certificates array`);
          } else if (Array.isArray(certificatesData)) {
            extractedCertificates = certificatesData;
            console.log(`Found ${extractedCertificates.length} certificates in direct array`);
          }
          
          setCertificates(extractedCertificates);
        } catch (certError) {
          console.error('Error fetching certificates:', certError);
          setCertificates([]);
          toast.warning('Could not load user certificates');
        }
      } catch (err) {
        console.error('Error fetching user details:', err);
        setError('Failed to load user details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserDetails();
  }, [userId]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        <span className="ml-3 text-gray-600">Loading user details...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
            <Link to="/admin/users" className="text-sm text-red-700 font-medium underline">
              Return to users list
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">User not found</p>
            <Link to="/admin/users" className="text-sm text-yellow-700 font-medium underline">
              Return to users list
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Handle certificate approval
  const handleApprove = async (certificateId) => {
    try {
      await adminService.verifyCertificate(certificateId, { isApproved: true });
      toast.success('Certificate approved successfully');
      
      // Refresh the page or reload certificates
      const userData = await adminService.getUserById(userId);
      setUser(userData.user || userData);
      
      // Reload certificates
      const certificatesData = await adminService.getUserCertificates(userId);
      console.log('Reloaded certificates after approval:', certificatesData);
      
      // Extract certificates array
      let extractedCertificates = [];
      if (certificatesData?.certificates && Array.isArray(certificatesData.certificates)) {
        extractedCertificates = certificatesData.certificates;
      } else if (Array.isArray(certificatesData)) {
        extractedCertificates = certificatesData;
      }
      
      setCertificates(extractedCertificates);
    } catch (error) {
      console.error('Error approving certificate:', error);
      toast.error('Failed to approve certificate');
    }
  };
  
  // Handle certificate rejection
  const handleReject = async (certificateId) => {
    try {
      await adminService.verifyCertificate(certificateId, { isApproved: false });
      toast.success('Certificate rejected successfully');
      
      // Reload certificates
      const certificatesData = await adminService.getUserCertificates(userId);
      
      // Extract certificates array
      let extractedCertificates = [];
      if (certificatesData?.certificates && Array.isArray(certificatesData.certificates)) {
        extractedCertificates = certificatesData.certificates;
      } else if (Array.isArray(certificatesData)) {
        extractedCertificates = certificatesData;
      }
      
      setCertificates(extractedCertificates);
    } catch (error) {
      console.error('Error rejecting certificate:', error);
      toast.error('Failed to reject certificate');
    }
  };

  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      {/* User Detail Header */}
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">User Profile</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Detailed information and certificates.</p>
        </div>
        <Link to="/admin/users" className="text-primary-600 hover:text-primary-800 text-sm font-medium">
          ← Back to Users List
        </Link>
      </div>
      
      {/* User Details Section */}
      <div className="border-t border-gray-200">
        <dl>
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Full name</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.name}</dd>
          </div>
          
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Email address</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{user.email}</dd>
          </div>
          
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Role</dt>
            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                ${user.role === 'donor' ? 'bg-yellow-100 text-yellow-800' : 
                  user.role === 'recipient' ? 'bg-blue-100 text-blue-800' : 
                  'bg-purple-100 text-purple-800'}`}>
                {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
              </span>
            </dd>
          </div>
          
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Organization</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {user.organization || 'Not specified'}
            </dd>
          </div>
          
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Account status</dt>
            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                ${user.is_active || user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                {user.is_active || user.isActive ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>
          
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Email verification</dt>
            <dd className="mt-1 text-sm sm:mt-0 sm:col-span-2">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                ${user.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                {user.email_verified ? 'Verified' : 'Not Verified'}
              </span>
            </dd>
          </div>
          
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Phone number</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {user.phone || 'Not provided'}
            </dd>
          </div>
          
          <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Address</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {user.address || 'Not provided'}
            </dd>
          </div>
          
          <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
            <dt className="text-sm font-medium text-gray-500">Joined date</dt>
            <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
              {formatDate(user.created_at || user.createdAt)}
            </dd>
          </div>
        </dl>
      </div>
      
      {/* Certificates Section (if user is a donor) */}
      {user.role === 'donor' && (
        <div className="border-t border-gray-200">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">Food Safety Certificates</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Verification documents submitted by this donor.
            </p>
            <div className="mt-2">
              <div className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium ${user.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                Verification Status: {user.email_verified ? 'Verified' : 'Not Verified'}
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
            {certificates.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Certificate
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Issued By
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Issue Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {certificates.map((certificate) => (
                      <tr key={certificate.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {certificate.certificate_number || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {certificate.issuer}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                            ${certificate.status === 'approved' 
                              ? 'bg-green-100 text-green-800' 
                              : certificate.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-red-100 text-red-800'}`}
                          >
                            {certificate.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(certificate.issue_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(certificate.expiry_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <a
                            href={certificate.document_url || certificate.fileUrl || certificate.documentUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-900 mr-4"
                          >
                            View
                          </a>
                          {certificate.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(certificate.id)}
                                className="text-green-600 hover:text-green-900 mr-4"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleReject(certificate.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-500">No certificates found for this user.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetailView;
